<template>
  <ModalBase show-modal :loading="loading" auto-h narrow @close-modal="close" class="gk-modal-file-detail">
    <template v-if="!error && !loading" v-slot:maincontent>
      <div class="content content--left">
        <div class="item">
          <h3>
            {{ $gettext('File details') }}
          </h3>
        </div>
        <div class="item">
          <div class="label">
            {{ $gettext('Name') }}
          </div>
          <div class="value">
            {{ getFileName(item) }}
          </div>
        </div>

        <div class="item">
          <div class="label">
            {{ $gettext('Created on') }}
          </div>
          <div class="value">
            {{ item.created | formatDate }}
          </div>
        </div>

        <div class="item">
          <div class="label">
            {{ $gettext('Valid until') }}
          </div>
          <div class="value">
            {{ item.expiry | formatDate }}
          </div>
        </div>

        <div class="item">
          <div class="label">
            {{ $gettext('Created by') }}
          </div>
          <div class="value d-flex align-items-center">
            <GkAvatar :userId="item.owner.id" />
            {{ item.owner.display_name }}
          </div>
        </div>

        <div class="item">
          <div class="label">
            {{ $gettext('Origin') }}
          </div>
          <div class="value">
            {{ item.subtype }}
          </div>
        </div>

        <div class="item">
          <div class="label">
            {{ $gettext('File type') }}
          </div>
          <div class="value">
            {{ extensionFromMimeType(item.mime_type).slice(1) }}
          </div>
        </div>
      </div>
      <div class="content content--right">
        <!-- file STATUS -->
        <div class="content--right__status">
          <!-- badge premium -->
          <div v-if="getSecondsLeft(item) > 0" class="activation-progress">
            <CircularProgressBar :value="getTimeoutPercentage(item)" bg-color="#f3f4f5">
              <GkBadge
                :id="`timeout-hint-${item.uuid}`"
                icon="fa-solid fa-crown"
                icon-only
                type="premium"
                variant="light"
                :tooltip-text="
                  $gettext(
                    'This download is in the processing queue, upgrade to premium to skip the line and download any file immediately.'
                  )
                "
              />
            </CircularProgressBar>
          </div>

          <!-- badge processing -->
          <div
            v-if="getSecondsLeft(item) <= 0 && item.status === 'processing'"
            class="status-badge"
            :class="'status-' + item.status"
          >
            <div class="status-circle-badge badge-spinner" style="width: 13px; height: 13px" />
            {{ getStatusBadgeText(item.status) }}
          </div>

          <!-- hint processing -->
          <div v-if="getSecondsLeft(item) <= 0 && item.status === 'processing'" class="status-hint">
            {{ $gettext('This file will be ready to download shortly.') }}
          </div>

          <!-- hint active -->
          <div v-if="getSecondsLeft(item) <= 0 && item.status === 'active'" class="status-hint status-hint--active">
            <font-awesome-icon icon="fa-regular fa-circle-check" />
            {{ $gettext('Your file is ready!') }}
          </div>

          <!-- hint expired -->
          <div v-if="item.status === 'expired'" class="status-hint status-hint--expired">
            <font-awesome-icon icon="fa-regular fa-circle-xmark" />
            {{ $gettext('This file is no longer available, please download it again.') }}
          </div>
        </div>

        <!-- premium CTA -->
        <div v-if="!currentCompany.is_premium && getSecondsLeft(item) > 0" class="content--right__cta">
          <div class="cta--text">
            <span>
              {{
                $gettext(
                  'This download is in the processing queue, upgrade to premium to skip the line and download any file immediately.'
                )
              }}
            </span>
            <GekkoButton
              data-analytics-click="Files modal: upgrade"
              :href="Urls['subscriptions:manage']()"
              :text="$gettext('Upgrade now')"
              variant="link"
              class="px-0"
            />
          </div>
        </div>

        <!-- actions -->
        <div class="content--right__buttons">
          <div v-if="item.status !== 'expired'">
            <GekkoButton
              :disabled="item.status === 'processing'"
              @click="download"
              :text="$gettext('Download')"
              icon="fa fa-download"
            />
            <div>
              <GekkoButton
                :disabled="item.status === 'processing'"
                @click="copyLink"
                :text="$gettext('Copy public link')"
                icon="fa-regular fa-copy"
                variant="link"
                class="p-0"
                size="sm"
              />
              <GekkoButton
                :disabled="item.status === 'processing'"
                icon="fa fa-question-circle"
                variant="link"
                class="text-blue px-0"
                v-b-tooltip.hover="
                  $gettext(
                    'With the public link you can share your download data quickly and safely with anyone else, regardless of whether they have  a Gekko account or are connected to your account.'
                  )
                "
              />
            </div>
          </div>
          <!-- expired temporarily disabled -->
          <!-- <div v-else>
            <div class="status-hint">
              {{ $gettext('This file is no longer available') }}
            </div>
            <GekkoButton @click="regenerate" :text="$gettext('Reactivate')" icon="fa fa-refresh" />
          </div> -->
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters } from 'vuex'
import { extensionFromMimeType, getFileName, getSecondsLeft } from '@/utils'

import ModalBase from '@/components/base_components/ModalBase.vue'
import TitleBar from '@/components/TitleBar.vue'
import GekkoButton from '@/components/GekkoButton.vue'
import CircularProgressBar from '@/components/ui/CircularProgressBar.vue'

import TemporaryFileService from '@/store/services/temporaryfile.service'

export default {
  name: 'FileDetailModal',
  components: { GekkoButton, TitleBar, ModalBase, CircularProgressBar },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
    }),
  },
  data() {
    return {
      loading: true,
      error: null,
      item: null,
      interval: null,
      secondsLeft: null,
    }
  },
  created() {
    this.setupData()
  },
  mounted() {},
  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  methods: {
    extensionFromMimeType,
    getFileName,
    getSecondsLeft,
    close() {
      this.$emit('close')
    },
    setupData() {
      TemporaryFileService.getById(this.id)
        .then((response) => {
          this.item = response.data
          if (this.getSecondsLeft(this.item) > 0) {
            this.setCounterInterval(this.item)
          }
        })
        .catch(() => {
          this.error = 'gone'
        })
        .finally(() => {
          this.loading = false
        })
    },
    setCounterInterval(item) {
      this.interval = setInterval(() => {
        this.secondsLeft = this.getSecondsLeft(item)
        if (this.secondsLeft <= 0) {
          clearInterval(this.interval)
          this.interval = null
        }
      }, 1000)
    },
    getTimeoutPercentage(item) {
      return 100 - (this.secondsLeft / item.activate_after_seconds) * 100
    },
    download() {
      if (this.item.status === 'processing') return

      // Safari detection (includes iOS Safari). Excludes Chrome/Firefox on iOS.
      const ua = navigator.userAgent
      const isSafari =
        /Safari/i.test(ua) &&
        !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS|Android/i.test(ua)

      // Only for Safari: open a tab synchronously to preserve user gesture
      const popup = isSafari ? window.open('', '_blank') : null
      if (popup) popup.opener = null

      TemporaryFileService.download(this.item.uuid)
        .then((response) => {
          const requestedFileUrl = response.data

          if (popup && !popup.closed) {
            try {
              popup.document.title = this.$gettext('Preparing download…')
              popup.document.body.innerHTML =
                `<p>${this.$gettext('Preparing your download...')}</p>` +
                `<p>${this.$gettext('You can close this tab once the download has started.')}</p>`
            } catch (_) {
              // ignore (some browsers may block access)
            }

            popup.location.href = requestedFileUrl
            popup.focus?.()
            return
          }

          // Non-Safari (or popup blocked): keep current link technique
          const link = document.createElement('a')
          link.href = requestedFileUrl
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch(() => {
          // Close the blank tab if we opened one
          if (popup && !popup.closed) popup.close()

          this.$store.commit('setAlert', {
            title: this.$gettext('Failed to download your file'),
            text: this.$gettext('Could not download your file, please try again later.'),
            variant: 'danger',
            duration: 4,
          })
        })
    },
    copyLink() {
      if (!this.currentCompany.is_premium && !this.currentUser.is_accountant) {
        return this.$store.commit('togglePremiumModal', 'web_files_share')
      }

      navigator.clipboard.writeText(window.location.origin + window.Urls['file_download'](this.item.uuid))

      this.$store.commit('setAlert', {
        title: this.$gettext('Copied to clipboard'),
        text: this.$gettext(
          'A public link has been copied to your clipboard, use this to share the file with other people.'
        ),
        variant: 'success',
        duration: 4,
      })
    },
    regenerate() {
      if (!this.currentCompany.is_premium) {
        return this.$store.commit('togglePremiumModal', 'web_files_regenerate')
      }

      TemporaryFileService.regenerate(this.item.uuid)
        .then(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Download regenerated'),
            text: this.$gettext('Your download has been successfully regenerated, please check back in a moment'),
            variant: 'success',
            duration: 4,
          })
        })
        .then(() => {
          this.$emit('regenerate')
        })
        .then(() => {
          if (this.$route.name === 'ControlPanelFileDetails') {
            this.$router.push({ name: 'ControlPanelFiles' })
          } else {
            this.$router.push({ name: 'Files' })
          }
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Regeneration failed'),
            text: this.$gettext('Failed to regenerate your download. Please try again later.'),
            variant: 'danger',
            duration: 4,
          })
        })
    },
    getStatusBadgeText(type) {
      let badgeText = ''
      switch (type) {
        case 'processing':
          badgeText = this.$gettext('Processing')
          break
        case 'expired':
          badgeText = this.$gettext('Expired')
          break
        case 'active':
          badgeText = this.$gettext('Ready')
          break
      }
      return badgeText
    },
  },
}
</script>
<style lang="scss">
.gk-modal-file-detail {
  .gk-modal-wrapper {
    .gk-modal {
      .gk-modal-content {
        .loading-spinner {
          margin: -1rem;
          width: calc(100% + 2rem);
          height: calc(100% + 2rem);
          border-radius: $radiusLg;
        }
        .modal-content-wrap {
          padding: 0;
          margin: -1rem;
          border-radius: 12px;
          display: flex;
          min-height: 380px;
          overflow: hidden;
        }
      }
      .action-buttons-wrapper {
        display: flex;
        gap: 0.75rem;
        justify-content: flex-end;
      }
    }
  }

  .content {
    .item {
      .value {
        gap: 0.5rem;
        .avatar,
        .no-avatar {
          margin: 0;
          padding: 0;
          width: 30px;
          height: 30px;
          min-width: 30px;
          min-height: 30px;
        }
        .no-avatar {
          border-radius: 50em;
          display: flex;
          justify-content: center;
          align-items: center;
          color: $colorTextSemi;
          background: #f5f5f5;
        }
      }
    }
  }
}
</style>
<style scoped lang="scss">
.content {
  &--left {
    width: 45%;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  &--right {
    width: 55%;
    background: #f4f8ff;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    &__cta {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 1rem;
      // padding: 1rem 0;
      .cta--text {
        font-size: 1rem;
        text-align: center;

        padding: 1rem;
        border-radius: $radiusLg;
        background: white;
        box-shadow: $toastShadow;
      }
    }
    &__status {
      padding: 1rem 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;

      .activation-progress {
        width: 55px;
        height: 55px;
        border-radius: 50em;
        outline: 1px solid #eeeeee;
        .circular-progress {
          width: 55px !important;
          height: 55px !important;
          .gk-badge {
            transform: scale(1.5) !important;
          }
        }
      }

      .status-badge {
        transform: scale(1.25);
      }
      .status-hint {
        text-align: center;
        font-size: 1rem;
        // margin-bottom: 1rem;
        &--active,
        &--expired {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          .svg-inline--fa {
            font-size: 30px;
          }
        }
        &--active {
          .svg-inline--fa {
            color: $colorBrand;
            opacity: 0.5;
          }
        }
        &--expired {
          .svg-inline--fa {
            color: $colorDanger;
            opacity: 0.5;
          }
        }
      }
    }
    &__buttons {
      padding-top: 1rem;
      > div {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    }
  }

  .item {
    display: flex;
    flex-direction: column;
    h3 {
      font-size: 22px;
    }
    .label {
      font-size: 14px;
      color: $colorTextSemi;
    }
    .value {
      font-size: 14px;
      display: flex;
    }
  }
}
</style>